import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Upload, Sparkles, Save, Camera, X, RefreshCw } from "lucide-react";
import { useCameraScanner } from "@/hooks/useCameraScanner";

const EXTRACTED_FIELDS = [
  ["Nome", "first_name"], ["Cognome", "last_name"],
  ["Tipo documento", "document_type"], ["Numero", "document_number"],
  ["Sesso", "gender"], ["Nazionalità", "nationality"],
  ["Data nascita", "birth_date"], ["Luogo nascita", "birth_place"],
  ["Data emissione", "issue_date"], ["Data scadenza", "expiry_date"],
  ["Autorità", "issuing_authority"],
];

function buildGuestPayload(data) {
  return {
    first_name: data.first_name || "",
    last_name: data.last_name || "",
    document_type: data.document_type || "carta_identita",
    document_number: data.document_number || "",
    nationality: data.nationality || "ITA",
    birth_date: data.birth_date || null,
    birth_place: data.birth_place || null,
    gender: data.gender || "M",
    issue_date: data.issue_date || null,
    expiry_date: data.expiry_date || null,
    issuing_authority: data.issuing_authority || null,
  };
}

export default function GuestScanner() {
  const nav = useNavigate();
  const { videoRef, active, error, startCamera, capture, stop } = useCameraScanner();
  const [mode, setMode] = useState("idle"); // idle | camera | preview
  const [items, setItems] = useState([]); // [{id, name, dataUrl, b64, scanning, extracted, error}]
  const [scanningAll, setScanningAll] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [mismatches, setMismatches] = useState([]); // campi dove i file forniscono valori diversi
  const [showConfirm, setShowConfirm] = useState(false);

  const handleStart = useCallback(async () => {
    await startCamera();
    setMode("camera");
  }, [startCamera]);

  // Surface camera-permission errors only when they happen, not on every render.
  useEffect(() => {
    if (error === "camera_denied" && mode === "camera") {
      toast.error("Impossibile accedere alla fotocamera. Controlla i permessi.");
    }
  }, [error, mode]);

  const addItem = (name, dataUrl, b64) => {
    const id = Math.random().toString(36).slice(2, 10);
    setItems(prev => [...prev, { id, name, dataUrl, b64, scanning: false, extracted: null, error: null }]);
    setMode("preview");
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(it => it.id !== id));
    // Se rimuoviamo l'ultimo torniamo in idle
    setTimeout(() => {
      setItems(curr => {
        if (curr.length === 0) setMode("idle");
        return curr;
      });
    }, 0);
  };

  const handleCapture = useCallback(() => {
    const result = capture();
    if (!result) return;
    const n = items.length + 1;
    addItem(`Fotocamera ${n}`, result.dataUrl, result.base64);
    stop();
  }, [capture, items.length, stop]);

  const handleFile = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => processFile(f));
    e.target.value = "";
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrop = useCallback((file) => {
    processFile(file);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const processFile = (file) => {
    if (!file) return;
    if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
      toast.error(`${file.name}: i PDF non sono ancora supportati. Fai una foto della prima pagina.`);
      return;
    }
    if (file.type && !file.type.startsWith("image/")) {
      toast.error(`${file.name}: formato non supportato (serve JPG/PNG/WEBP/HEIC).`);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error(`${file.name}: file troppo grande (max 15 MB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const idx = result.indexOf(",");
      const b64 = idx >= 0 ? result.slice(idx + 1) : result;
      addItem(file.name, result, b64);
    };
    reader.onerror = () => toast.error(`Impossibile leggere ${file.name}.`);
    reader.readAsDataURL(file);
  };

  /**
   * Fonde i risultati OCR di più file in un unico record.
   * Strategia: per ogni campo prende il primo valore non-null nell'ordine di scansione.
   * Rileva campi "mismatch" (es. due documenti con `last_name` diverso) e li ritorna
   * in `mismatches` così la UI può mostrare un avviso all'host.
   */
  const mergeExtracted = (results) => {
    const merged = {};
    const conflicts = [];
    const fields = EXTRACTED_FIELDS.map(([_, k]) => k);
    for (const key of fields) {
      const values = results
        .map(r => r && r[key])
        .filter(v => v !== null && v !== undefined && v !== "");
      if (values.length === 0) { merged[key] = null; continue; }
      merged[key] = values[0];
      // Flag mismatch solo sui campi identificativi forti
      if (["first_name", "last_name", "document_number", "birth_date"].includes(key)) {
        const uniq = [...new Set(values.map(v => String(v).trim().toLowerCase()))];
        if (uniq.length > 1) conflicts.push({ field: key, values });
      }
    }
    return { merged, conflicts };
  };

  const handleScan = useCallback(async () => {
    if (items.length === 0) return;
    setScanningAll(true);
    const results = [];
    // Scanner sequenziale: ogni foto va a /scan/document una alla volta
    for (const it of items) {
      if (it.extracted) { results.push(it.extracted); continue; }
      setItems(prev => prev.map(x => x.id === it.id ? { ...x, scanning: true, error: null } : x));
      try {
        const { data } = await api.post("/scan/document", { image_base64: it.b64 });
        setItems(prev => prev.map(x => x.id === it.id ? { ...x, scanning: false, extracted: data } : x));
        results.push(data);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Errore scansione";
        setItems(prev => prev.map(x => x.id === it.id ? { ...x, scanning: false, error: msg } : x));
        toast.error(`${it.name}: ${msg}`);
      }
    }
    setScanningAll(false);
    if (results.length === 0) return;
    const { merged, conflicts } = mergeExtracted(results);
    setExtracted(merged);
    setMismatches(conflicts);
    if (conflicts.length > 0) {
      toast.warning(`${results.length} documenti analizzati · ${conflicts.length} campi con valori diversi — controlla bene`);
    } else {
      toast.success(`${results.length} ${results.length === 1 ? "documento analizzato" : "documenti analizzati e uniti"} · conferma per salvare`);
    }
    setShowConfirm(true);
  }, [items]);

  const handleConfirmSave = useCallback(async () => {
    if (!extracted) return;
    try {
      const { data } = await api.post("/guests", buildGuestPayload(extracted));
      toast.success(`Ospite ${data.first_name} ${data.last_name} salvato in rubrica`);
      setShowConfirm(false);
      setTimeout(() => nav("/app/guests"), 600);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  }, [extracted, nav]);

  const reset = useCallback(() => {
    stop();
    setItems([]);
    setExtracted(null);
    setMismatches([]);
    setMode("idle");
  }, [stop]);

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">SCANSIONA DOCUMENTO</p>
      <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2">Foto al documento → ospite registrato</h1>
      <p className="text-slate-600 mb-6">Fotografa la carta d'identità o il passaporto (anche fronte+retro insieme): i dati vengono estratti dall'AI, uniti in un unico record e <strong>tu li confermi</strong> prima del salvataggio.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <ScannerPanel
          mode={mode} active={active} items={items} scanning={scanningAll}
          videoRef={videoRef}
          cameraError={error}
          onStart={handleStart} onCapture={handleCapture}
          onScan={handleScan} onFile={handleFile} onDrop={handleDrop}
          onRemoveItem={removeItem} onReset={reset}
        />
        <ExtractedPanel
          extracted={extracted} setExtracted={setExtracted}
          onReview={() => setShowConfirm(true)}
          sourcesCount={items.filter(i => i.extracted).length}
        />
      </div>

      <ConfirmGuestDialog
        open={showConfirm}
        extracted={extracted}
        setExtracted={setExtracted}
        mismatches={mismatches}
        sourcesCount={items.filter(i => i.extracted).length}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

function ScannerPanel({ mode, items, scanning, videoRef, cameraError, onStart, onCapture, onScan, onFile, onDrop, onRemoveItem, onReset }) {
  const permissionDenied = cameraError === "camera_denied" || cameraError === "camera_unavailable";
  const [isDragging, setIsDragging] = useState(false);

  const dragProps = {
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); },
    onDragOver:  (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); },
    onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      files.forEach((f) => onDrop(f));
    },
  };
  return (
    <div className="border border-slate-200 bg-white p-6 rounded-xl">
      <div className="text-xs tracked text-slate-500 mb-4">DOCUMENTO</div>

      {/* IDLE: due card grandi e chiare — scegli come caricare il documento */}
      {mode === "idle" ? (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            {/* Card 1: Fotocamera */}
            <button
              type="button"
              onClick={onStart}
              data-testid="start-camera"
              className="group relative text-left border-2 border-slate-200 hover:border-blue-600 bg-gradient-to-br from-blue-50/30 to-white rounded-xl p-5 transition-all hover:shadow-lg"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <Camera className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <div className="font-heading font-semibold text-base text-slate-900 mt-3">Usa la fotocamera</div>
              <p className="text-xs text-slate-600 mt-1 leading-snug">
                Scatta subito una foto al documento. Al primo utilizzo il browser ti chiede il permesso: tocca <strong>Consenti</strong>.
              </p>
              <div className="text-xs text-blue-700 mt-3 font-medium inline-flex items-center gap-1">
                Avvia fotocamera →
              </div>
            </button>

            {/* Card 2: Carica file (con drag & drop) */}
            <label
              {...dragProps}
              className={`group relative text-left border-2 ${isDragging ? "border-blue-600 bg-blue-50 scale-[1.02] shadow-lg" : "border-slate-200 hover:border-stone-900 bg-gradient-to-br from-stone-50 to-white hover:shadow-lg"} rounded-xl p-5 transition-all cursor-pointer`}
              data-testid="upload-doc"
            >
              <div className={`w-12 h-12 rounded-xl ${isDragging ? "bg-blue-600" : "bg-stone-900"} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-all`}>
                <Upload className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <div className="font-heading font-semibold text-base text-slate-900 mt-3">
                {isDragging ? "Rilascia qui per caricare" : "Carica un'immagine"}
              </div>
              <p className="text-xs text-slate-600 mt-1 leading-snug">
                {isDragging ? (
                  <span className="text-blue-700 font-medium">Lascia andare per leggere il documento</span>
                ) : (
                  <>
                    <span className="hidden sm:inline">Trascina una o più foto (fronte+retro, passaporto, patente…) o scegli dalla galleria.</span>
                    <span className="sm:hidden">Scegli una o più foto dalla galleria o dai file.</span>
                    <br />
                    <span className="text-slate-500">JPG · PNG · WEBP · HEIC (iPhone) · max 15 MB ciascuna</span>
                  </>
                )}
              </p>
              {!isDragging && (
                <div className="text-xs text-stone-900 mt-3 font-medium inline-flex items-center gap-1">
                  Scegli file →
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                multiple
                onChange={onFile}
                className="hidden"
                data-testid="upload-doc-input"
              />
            </label>
          </div>

          {/* Banner permessi camera: appare se l'utente ha negato in passato */}
          {permissionDenied && (
            <div className="mt-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-3 text-xs leading-relaxed" data-testid="camera-permission-help">
              <div className="font-semibold mb-1">⚠️ Fotocamera bloccata dal browser</div>
              Non si apre perché avevi negato il permesso. Per sbloccarla:
              <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                <li>Tocca il lucchetto 🔒 (o il simbolo info ⓘ) accanto all'indirizzo del sito in alto</li>
                <li>Cerca <strong>Fotocamera</strong> e metti <strong>Consenti</strong></li>
                <li>Ricarica la pagina</li>
              </ol>
              <div className="mt-2 text-amber-800">In alternativa usa <strong>Carica un'immagine</strong> qui sopra.</div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* CAMERA: inquadratura della fotocamera */}
          {mode === "camera" && (
            <>
              <div className="relative aspect-[4/3] border border-dashed border-slate-300 bg-stone-900 flex items-center justify-center overflow-hidden rounded-lg">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" data-testid="camera-video" />
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="relative w-[85%] h-[65%] border-2 border-blue-500/80 rounded-lg">
                    <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-sky-300" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-sky-300" />
                    <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-sky-300" />
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-sky-300" />
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={onCapture} data-testid="capture-photo"
                  className="inline-flex items-center gap-2 bg-blue-700 text-stone-50 px-5 py-2 text-sm rounded-lg hover:bg-blue-800">
                  <Camera className="w-4 h-4" /> Scatta foto
                </button>
                <button onClick={onReset} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm rounded-lg hover:bg-slate-50">
                  <X className="w-4 h-4" /> Annulla
                </button>
              </div>
            </>
          )}

          {/* PREVIEW: lista di documenti caricati con thumbnail + per-item status */}
          {mode === "preview" && (
            <>
              <div className="mb-2 text-xs text-slate-500">
                {items.length} {items.length === 1 ? "documento in coda" : "documenti in coda"}
                {items.length > 1 && " · verranno uniti in un unico ospite"}
              </div>
              <ul className="space-y-2" data-testid="scan-queue">
                {items.map((it) => (
                  <li key={it.id}
                    className="flex items-center gap-3 border border-slate-200 rounded-lg p-2 bg-white"
                    data-testid={`queue-item-${it.id}`}>
                    <img src={it.dataUrl} alt={it.name} className="w-16 h-16 object-cover rounded border border-slate-200" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{it.name}</div>
                      <div className="text-xs mt-0.5">
                        {it.scanning && <span className="text-blue-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3 animate-pulse" /> Scansione in corso…</span>}
                        {!it.scanning && it.extracted && (
                          <span className="text-emerald-700">
                            ✓ {it.extracted.first_name || "?"} {it.extracted.last_name || ""} · {it.extracted.document_type || "doc"}
                          </span>
                        )}
                        {!it.scanning && it.error && <span className="text-red-700">⚠ {it.error}</span>}
                        {!it.scanning && !it.extracted && !it.error && <span className="text-slate-500">In attesa di scansione</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveItem(it.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-slate-400 hover:text-red-700"
                      data-testid={`queue-remove-${it.id}`}
                      aria-label={`Rimuovi ${it.name}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={onScan} disabled={scanning || items.length === 0} data-testid="scan-button"
                  className="inline-flex items-center gap-2 bg-blue-700 text-stone-50 px-5 py-2 text-sm rounded-lg hover:bg-blue-800 disabled:opacity-60">
                  <Sparkles className="w-4 h-4" /> {scanning ? "Scansione in corso…" : `Scansiona ${items.length > 1 ? `${items.length} documenti` : "con AI"}`}
                </button>
                <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-4 py-2 text-sm rounded-lg hover:bg-slate-50" data-testid="add-more">
                  <Upload className="w-4 h-4" /> Aggiungi un'altra foto
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*" multiple onChange={onFile} className="hidden" />
                </label>
                <button onClick={onStart} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm rounded-lg hover:bg-slate-50" data-testid="add-camera">
                  <Camera className="w-4 h-4" /> Scatta un'altra
                </button>
                <button onClick={onReset} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm rounded-lg hover:bg-slate-50">
                  <RefreshCw className="w-4 h-4" /> Ricomincia
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ExtractedPanel({ extracted, setExtracted, onReview, sourcesCount }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="text-xs tracked text-slate-500 mb-4 flex items-center gap-2">
        <ScanLine className="w-4 h-4" />
        DATI ESTRATTI
        {sourcesCount > 1 && (
          <span className="text-blue-700 font-medium normal-case tracking-normal">
            · uniti da {sourcesCount} documenti
          </span>
        )}
      </div>
      {!extracted ? (
        <div className="text-slate-500 text-sm">Esegui una scansione per visualizzare i dati.</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 text-sm" data-testid="extracted-fields">
          {EXTRACTED_FIELDS.map(([label, key]) => (
            <label key={key} className="block">
              <span className="text-xs text-slate-500">{label}</span>
              <input value={extracted[key] || ""}
                onChange={(e) => setExtracted({ ...extracted, [key]: e.target.value })}
                className="w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white" />
            </label>
          ))}
          <div className="col-span-2 mt-2">
            <button data-testid="save-extracted" onClick={onReview}
              className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-lg hover:bg-stone-800">
              <Save className="w-4 h-4" /> Rivedi e conferma
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmGuestDialog({ open, extracted, setExtracted, mismatches, sourcesCount, onConfirm, onCancel }) {
  if (!open || !extracted) return null;
  const missing = [];
  if (!extracted.first_name) missing.push("Nome");
  if (!extracted.last_name) missing.push("Cognome");
  if (!extracted.document_number) missing.push("Numero documento");

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onCancel} data-testid="confirm-guest-backdrop">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[90vh] flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="confirm-guest-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading font-semibold text-xl">Conferma i dati dell'ospite</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {sourcesCount > 1
                ? `Dati uniti da ${sourcesCount} documenti. Controlla ed eventualmente correggi prima di salvare.`
                : "Controlla ciò che è stato estratto. Correggi eventuali errori prima di salvare in rubrica."}
            </p>
          </div>
          <button onClick={onCancel} aria-label="Annulla"
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {missing.length > 0 && (
            <div className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2.5 rounded-lg text-sm" data-testid="confirm-guest-warning">
              Campi obbligatori mancanti: <strong>{missing.join(", ")}</strong>. Compilali prima di confermare.
            </div>
          )}
          {mismatches && mismatches.length > 0 && (
            <div className="mb-4 border border-orange-300 bg-orange-50 text-orange-900 px-4 py-2.5 rounded-lg text-sm" data-testid="confirm-guest-mismatches">
              <div className="font-semibold mb-1">⚠ I documenti caricati contengono dati diversi su {mismatches.length} {mismatches.length === 1 ? "campo" : "campi"}</div>
              <ul className="ml-4 list-disc text-xs space-y-0.5">
                {mismatches.map((m) => (
                  <li key={m.field}>
                    <strong>{(EXTRACTED_FIELDS.find(f => f[1] === m.field) || [m.field])[0]}</strong>: {m.values.join(" / ")}
                  </li>
                ))}
              </ul>
              <div className="text-xs mt-2 text-orange-800">
                Ho tenuto il valore del <strong>primo documento</strong>. Verifica che non siano documenti di persone diverse.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {EXTRACTED_FIELDS.map(([label, key]) => (
              <label key={key} className="block">
                <span className="text-xs text-slate-500">{label}</span>
                <input value={extracted[key] || ""}
                  onChange={(e) => setExtracted({ ...extracted, [key]: e.target.value })}
                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white"
                  data-testid={`confirm-field-${key}`} />
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Se un ospite con lo stesso numero documento esiste già nella tua rubrica, i suoi dati verranno <strong>aggiornati</strong> invece di creare un duplicato.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900" data-testid="confirm-guest-cancel">
            Annulla
          </button>
          <button onClick={onConfirm} disabled={missing.length > 0}
            data-testid="confirm-guest-save"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-blue-800 disabled:opacity-50">
            <Save className="w-4 h-4" /> Conferma e salva in rubrica
          </button>
        </div>
      </div>
    </div>
  );
}
